import PropTypes from 'prop-types'
import { useCallback, useMemo } from 'react'
import cn from 'classnames'
import { createUseStyles } from '../../../helpers/createStyles'
import { theme } from '../../../styles/theme'
import Section from '../../Section'
import useSnapshot from '../../../store/useSnapshot'
import { getSiteData } from '../../../store/layoutSlice'

const CustomColorButton = ({ colorGroup, onClick, last, onMouseEnter, onMouseLeave, index }) => {
  const snap = useSnapshot()
  const colorLabel = getSiteData(snap)?.colorLabel || 'color'
  const styles = useStyles()
  const colorCount = useMemo(() => colorGroup.colors?.length || 0, [colorGroup])
  const handleClick = useCallback(() => {
    onClick(colorGroup)
  }, [colorGroup])

  const handleMouseEnter = useCallback(() => {
    if (onMouseEnter) onMouseEnter(index)
  }, [index])

  const handleMouseLeave = useCallback(() => {
    if (onMouseLeave) onMouseLeave(index)
  }, [index])

  return (
    <button
      className={cn(styles.root, 'custom-color-button')}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Section grid noBottomMargin={last} className={styles.inner}>
        <div className={cn(styles.hoverLines, 'hover-lines')} />
        <div className={styles.meta}>
          <img
            className={styles.thumbnail}
            src={`${colorGroup.image.asset.url}?fit=crop&h=20&w=20`}
            alt={colorGroup.image.asset.url.alt}
          />
          <h3 className={styles.name}>{colorGroup.name}</h3>
        </div>
        <span className={styles.count}>
          {colorCount} {colorLabel.toLowerCase()}{colorCount === 1 ? '' : 's'}
        </span>
        <p className={styles.description}>{colorGroup.description}</p>
      </Section>
    </button>
  )
}

const useStyles = createUseStyles({
  root: {
    unset: 'all',
    width: '100%',
    textAlign: 'left'
  },
  inner: {
    alignItems: 'center',
    height: 58,
    border: '0px solid rgba(0,0,0,.1)',
    borderTopWidth: 1,
    position: 'relative',
    '*:last-of-type > &': {
      borderBottomWidth: 1
    },
    '*:last-of-type > & .hover-lines': {
      bottom: '-1px !important'
    },
    [theme.breakpoints.up('md')]: {
      height: 106
    }
  },
  hoverLines: {
    position: 'absolute',
    top: -1,
    left: 0,
    bottom: -3,
    right: 0,
    border: '0px solid rgba(0,0,0,1)',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    opacity: 0,
    transition: 'transform 0.4s ease-in-out, opacity 0.4s ease-in-out',
    pointerEvents: 'none'
  },
  count: {
    gridColumn: 'auto / span 4',
    order: 2,
    position: 'relative'
  },
  meta: {
    gridColumn: 'auto / span 4',
    order: 1,
    display: 'flex',
    alignItems: 'center'
  },
  name: {
    margin: '0 !important',
    lineHeight: 1,
    fontSize: 16,
    [theme.breakpoints.up('md')]: {
      fontSize: 24
    }
  },
  description: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'initial',
      order: 3,
      gridColumn: 'auto / span 4',
      margin: '0 !important'
    }
  },
  thumbnail: {
    borderRadius: 10,
    marginRight: theme.spacing(1)
  }
})

CustomColorButton.propTypes = {
  colorGroup: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  last: PropTypes.bool.isRequired
}

export default CustomColorButton
